// Quel solde le MOTEUR DE RÈGLES doit-il utiliser ?
//
// PROBLÈME (cas réel : comptes cramés annoncés « objectif atteint »)
// Le moteur (computeChallengeProgress / computeBufferStatus) raisonnait sur le
// solde DÉRIVÉ : initialBalance + Σ netPnl(trades importés). Or les endpoints
// Tradovate `*/list` ne renvoient que le jour courant : les trades antérieurs à
// la connexion manquent structurellement au journal. Quand ce sont des PERTES
// qui manquent, le dérivé est SURÉVALUÉ, et les deux verdicts se trompent dans
// le même sens :
//   - la target paraît franchie alors que le compte est en perte (-2 063 $ réel
//     contre une cible à +3 000 $) -> modale « Objectif atteint » sur un compte
//     cramé, et targetReachedAt posé DÉFINITIVEMENT ;
//   - le plancher de drawdown paraît intact alors que le solde broker est
//     dessous (47 937 $ pour un plancher à 48 000 $) -> breach jamais détecté.
//
// RÈGLE : quand le broker a parlé, c'est lui qui fait autorité, ici aussi.
// `displayBalance.ts` appliquait déjà ce principe à l'AFFICHAGE, mais s'en
// tenait volontairement à l'écart du moteur par crainte d'une lecture throttlée
// (au plus une par heure, quota Tradovate 600 req/h par IP partagée).
//
// Ce raisonnement confondait deux choses distinctes :
//   - FRAÎCHEUR : le solde broker peut dater d'une heure ;
//   - EXACTITUDE : le solde dérivé est faux tant qu'il manque des trades.
// Une lecture d'il y a une heure reste bien plus juste qu'un journal incomplet.
// On sépare donc les deux usages :
//   - LECTURE (progression, plancher affiché, warnings) : le broker fait
//     autorité dès qu'il existe -> `resolveEngineBalance`.
//   - ACTION DESTRUCTRICE (désactiver un compte pour breach) : exiger EN PLUS
//     une lecture récente -> `isBalanceFreshEnoughToDeactivate`. Une donnée
//     périmée fait DIFFÉRER la décision au cycle suivant, elle ne la force pas.
//
// Module PUR : aucun import Wasp/Prisma, testable isolément.

/**
 * Âge maximum d'une lecture broker pour AUTORISER une désactivation
 * automatique.
 *
 * Calibré sur le cycle de sync (15 min) : deux cycles de retard tolérés, de quoi
 * absorber un cycle sauté ou un throttle du snapshot de cash sans jamais
 * désactiver sur une lecture vraiment périmée.
 */
export const BALANCE_FRESHNESS_MAX_AGE_MS = 45 * 60 * 1000;

export interface EngineBalanceInput {
  /** Solde dérivé : initialBalance + Σ trades − payouts. Toujours défini. */
  derivedBalance: number;
  /** Dernier solde lu chez le broker. Null si jamais lu ou compte manuel. */
  brokerBalance: number | null;
}

/**
 * Solde sur lequel le moteur de règles doit raisonner.
 *
 * Le solde broker l'emporte dès qu'il existe et qu'il est exploitable. Un compte
 * MANUEL n'a aucun broker à interroger : il garde le dérivé, qui est pour lui la
 * seule valeur ayant un sens — donc aucune régression pour les comptes manuels.
 *
 * Une valeur non finie (NaN, Infinity) est traitée comme absente.
 */
export function resolveEngineBalance(input: EngineBalanceInput): number {
  const { brokerBalance, derivedBalance } = input;
  if (brokerBalance === null || !Number.isFinite(brokerBalance)) return derivedBalance;
  return brokerBalance;
}

export interface BalanceFreshnessInput {
  /** Date de la dernière lecture broker. Null si jamais lue. */
  brokerBalanceAt: Date | null;
  /** Solde broker, pour distinguer « compte manuel » de « lecture périmée ». */
  brokerBalance: number | null;
  now: Date;
}

/**
 * Peut-on DÉSACTIVER un compte sur la foi du solde courant ?
 *
 * Un compte MANUEL (aucun solde broker) retourne true : son solde dérivé est sa
 * seule vérité et le comportement historique doit être conservé — sinon plus
 * aucun compte manuel ne serait jamais désactivé pour breach, ce qui serait une
 * régression fonctionnelle.
 *
 * Un compte SYNCHRONISÉ n'autorise la désactivation que si sa dernière lecture
 * est récente. Une lecture manquante, invalide, périmée, ou datée dans le futur
 * (dérive d'horloge) bloque la désactivation : le compte reste actif et sera
 * réévalué au cycle suivant. Différer est sans danger ; désactiver à tort sur
 * une donnée périmée ne se corrige pas tout seul.
 */
export function isBalanceFreshEnoughToDeactivate(input: BalanceFreshnessInput): boolean {
  const { brokerBalanceAt, brokerBalance, now } = input;

  // Compte manuel (ou synchronisé dont aucun snapshot n'a jamais été lu) :
  // comportement historique inchangé.
  if (brokerBalance === null || !Number.isFinite(brokerBalance)) return true;

  if (!(brokerBalanceAt instanceof Date) || Number.isNaN(brokerBalanceAt.getTime())) return false;

  const age = now.getTime() - brokerBalanceAt.getTime();
  if (age < 0) return false; // lecture dans le futur : horloge suspecte
  return age <= BALANCE_FRESHNESS_MAX_AGE_MS;
}

/**
 * Âge à partir duquel le solde broker est SIGNALÉ COMME DATÉ à l'utilisateur.
 *
 * Distinct — et volontairement plus large — que BALANCE_FRESHNESS_MAX_AGE_MS,
 * parce que les deux seuils répondent à des questions différentes :
 *
 *  - 45 min (désactivation) borne une ACTION IRRÉVERSIBLE. Être strict y est
 *    gratuit : au pire la décision est différée d'un cycle.
 *  - ce seuil-ci borne un AFFICHAGE. Être strict y coûte cher : une mention
 *    « donnée datée » qui s'allume en régime normal est un cri au loup, et
 *    l'utilisateur apprend à l'ignorer — y compris le jour où elle est vraie.
 *
 * Or le snapshot d'un compte gelé n'est relu qu'une fois par heure
 * (RESET_CHECK_INTERVAL_MS) : à 45 min, un compte parfaitement sain passerait
 * l'essentiel de son temps marqué « daté ». On prend donc 2 h 15, soit deux
 * lectures horaires manquées plus une marge de cycle. En dessous, le silence
 * est justifié ; au-dessus, quelque chose ne va réellement plus.
 */
export const BALANCE_STALE_DISPLAY_AGE_MS = 135 * 60 * 1000; // 2 h 15

export interface StaleBalanceDisplayInput {
  /** Horodatage de la dernière lecture broker. Null si jamais lue. */
  brokerBalanceAt: Date | null;
  /** Solde broker, pour distinguer « compte manuel » de « lecture périmée ». */
  brokerBalance: number | null;
  now: Date;
}

/**
 * Faut-il signaler à l'utilisateur que le solde affiché date ?
 *
 * Retourne false pour un compte MANUEL (aucun solde broker) : son solde dérivé
 * est sa seule vérité et n'a pas de notion de fraîcheur — afficher « donnée
 * datée » y serait un contresens.
 *
 * Retourne true quand la lecture est trop ancienne, mais AUSSI quand
 * l'horodatage est absent, invalide, ou situé dans le futur alors qu'un solde
 * broker existe. Ces trois cas signent une incohérence : ne rien dire
 * reviendrait à présenter une valeur d'âge inconnu comme fraîche, ce qui est
 * exactement ce qu'on cherche à éviter.
 *
 * Ce prédicat ne CHANGE JAMAIS le solde affiché : il ne fait que l'assortir
 * d'une réserve. Substituer silencieusement une autre valeur sous les yeux de
 * l'utilisateur serait une seconde information trompeuse, pas un correctif.
 */
export function isBalanceStaleForDisplay(input: StaleBalanceDisplayInput): boolean {
  const { brokerBalanceAt, brokerBalance, now } = input;

  // Compte manuel, ou synchronisé dont aucun snapshot n'a jamais été lu :
  // aucune fraîcheur à revendiquer, donc aucune réserve à afficher.
  if (brokerBalance === null || !Number.isFinite(brokerBalance)) return false;

  if (!(brokerBalanceAt instanceof Date) || Number.isNaN(brokerBalanceAt.getTime())) return true;
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return true;

  const age = now.getTime() - brokerBalanceAt.getTime();
  if (age < 0) return true; // lecture dans le futur : horloge suspecte
  return age > BALANCE_STALE_DISPLAY_AGE_MS;
}
